// src/components/Footer.js
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useContent } from '../../context/ContentContext';
import { fadeIn } from '../../utils/gsapAnimations';
import SectionLoader from '../common/SectionLoader';

function Footer() {
  const footerRef = useRef(null);
  const { content, loading } = useContent();

  useEffect(() => {
    fadeIn(footerRef.current, 0.4);
  }, []);

  const socialLinks = content?.settings?.social_links || [];

  if (loading) {
    return (
      <footer ref={footerRef} className="bg-primary text-cream py-12">
        <div className="max-w-7xl mx-auto px-6">
          <SectionLoader message="Loading footer..." />
        </div>
      </footer>
    );
  }

  return (
    <footer
      ref={footerRef}
      className="bg-primary text-cream py-12 sm:py-16 lg:py-20 mt-auto"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12">
          {/* Brand & Description */}
          <div className="space-y-6">
            <h3 className="text-2xl sm:text-3xl font-playfair-display font-semibold">
              {content.settings?.brand_name || 'MyShop'}
            </h3>
            <p className="text-base sm:text-lg font-montserrat leading-relaxed opacity-90 max-w-md">
              {content.settings?.footer_description || 'Test Description'}
            </p>
          </div>

          {/* Quick Links */}
          <div className="space-y-6">
            <h4 className="text-xl sm:text-2xl font-playfair-display font-semibold">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { to: '/shop', label: 'Shop' },
                { to: '/about', label: 'About Us' },
                // { to: '/sustainability', label: 'Sustainability' },
                { to: '/contact', label: 'Contact' },
                { to: '/faq', label: 'FAQ' },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-base sm:text-lg font-montserrat opacity-90 hover:opacity-100 hover:text-cream/100 transition-all duration-300 hover:translate-x-1 inline-block"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Social & Copyright */}
          <div className="space-y-6">
            <h4 className="text-xl sm:text-2xl font-playfair-display font-semibold">Connect With Us</h4>
            <div className="flex flex-wrap gap-4">
              {socialLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-white/10 backdrop-blur-sm rounded-full hover:bg-white/20 transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-cream/40"
                  aria-label={link.label}
                >
                  <img
                    src={`/assets/icons/${link.icon}`}
                    alt={link.label}
                    className="w-6 h-6 sm:w-7 sm:h-7"
                    onError={() => console.error(`Failed to load icon: ${link.icon}`)}
                  />
                </a>
              ))}
            </div>

            <div className="pt-6 border-t border-cream/20">
              <p className="text-sm sm:text-base font-montserrat opacity-90">
                © {new Date().getFullYear()} {content.settings?.brand_name || 'MyShop'}. All rights reserved.
              </p>
              <p className="text-sm font-montserrat opacity-70 mt-1">
                Powered by BizCart
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;